import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listReceipts,
  getLastExports,
  exportBundle,
  exportCsv,
  exportPackage,
  exportDownloads,
  downloadBlob,
  filenameFromResponse,
} from "@/lib/api";
import { formatMoney, formatDate, formatDateTime, effectiveValue, sortReceipts, sumByCurrency } from "@/lib/format";
import { SortHeader, SortMenu } from "@/components/SortControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CalendarRange,
  FileArchive,
  Sheet,
  RotateCcw,
  Hash,
  Wallet,
  Percent,
  Coins,
  CalendarClock,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (n) => {
  if (!n) return "–";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

function MiniStat({ icon: Icon, label, value, values, testId }) {
  const list = values ?? [value];
  const multi = list.length > 1;
  return (
    <div
      className="bg-primary border-[5px] border-white p-4 flex items-center gap-3 shadow-sm"
      data-testid={testId}
    >
      <div className="shrink-0">
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="min-w-0">
        <div className="data-label !text-white">{label}</div>
        {multi ? (
          <div className="mt-1 space-y-0.5">
            {list.map((s) => (
              <div
                key={s}
                className="font-heading text-base font-semibold tabular text-white leading-tight"
              >
                {s}
              </div>
            ))}
          </div>
        ) : (
          <div className="font-heading text-xl font-semibold tabular mt-0.5 text-white">
            {list[0]}
          </div>
        )}
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { key: "date", label: "Datum" },
  { key: "supplier", label: "Lieferant" },
  { key: "purpose", label: "Zweck" },
  { key: "price", label: "Brutto" },
];

export default function ExportCenter() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState("");
  const [lastExports, setLastExports] = useState({ pdf_bundle: null, csv_datev: null });
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc"); // default: newest date first

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  const loadLast = useCallback(async () => {
    try {
      setLastExports(await getLastExports());
    } catch {
      // Letzte Exporte sind optional – ein Fehler hier wird bewusst ignoriert.
    }
  }, []);

  const loadDownloads = useCallback(async () => {
    try {
      setDownloadLinks(await exportDownloads());
    } catch {
      // Optional – Fehler bewusst ignorieren.
    }
  }, []);

  const loadReceipts = useCallback(async () => {
    try {
      setReceipts(await listReceipts());
    } catch {
      toast.error("Belege konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReceipts();
    loadLast();
    loadDownloads();
  }, [loadReceipts, loadLast, loadDownloads]);

  const inRange = useCallback(
    (d) => {
      if (!dateFrom && !dateTo) return true;
      if (!d) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    },
    [dateFrom, dateTo]
  );

  const periodReceipts = useMemo(
    () => sortReceipts(receipts.filter((r) => inRange(r.date)), sortKey, sortDir),
    [receipts, inRange, sortKey, sortDir]
  );

  const stats = useMemo(() => {
    return {
      count: periodReceipts.length,
      totals: sumByCurrency(periodReceipts),
    };
  }, [periodReceipts]);

  const periodActive = Boolean(dateFrom || dateTo);

  const handleExportResponse = async (res, fallback, successMsg) => {
    if (res.status === 202) {
      let msg = "Der Export wird erstellt und Ihnen per E-Mail als Download-Link (7 Tage gültig) zugesendet.";
      try {
        const j = JSON.parse(await res.data.text());
        if (j?.message) msg = j.message;
      } catch {
        /* Blob war doch kein JSON – Standardtext verwenden. */
      }
      toast.success(msg);
    } else {
      downloadBlob(res.data, filenameFromResponse(res, fallback));
      toast.success(successMsg);
    }
    loadLast();
    loadDownloads();
    if (res.status === 202) {
      // Hintergrund-Erzeugung braucht kurz – Liste verzögert nachladen.
      setTimeout(loadDownloads, 5000);
      setTimeout(loadDownloads, 12000);
    }
  };

  const doExport = async (kind) => {
    if (periodReceipts.length === 0) {
      return;
    }
    setExporting(kind);
    try {
      let res;
      let fallback;
      if (kind === "csv") {
        res = await exportCsv(dateFrom, dateTo);
        fallback = "Besca_Buchhaltung.csv";
      } else {
        res = await exportBundle(dateFrom, dateTo, kind);
        fallback = kind === "zip" ? "Besca_Belege.zip" : "Besca_Sammelbeleg.pdf";
      }
      await handleExportResponse(res, fallback, "Export erstellt.");
    } catch {
      toast.error("Export fehlgeschlagen.");
    } finally {
      setExporting("");
    }
  };

  const lastLabel = (entry) => {
    if (!entry) return "Noch nie";
    return `${formatDateTime(entry.created_at)} · ${entry.count} Beleg(e) · ${(entry.format || "").toUpperCase()}`;
  };

  // First/last day of the PREVIOUS month as YYYY-MM-DD (local).
  const previousMonthRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: fmt(first), to: fmt(last) };
  };

  const doLastMonth = async () => {
    const { from, to } = previousMonthRange();
    setDateFrom(from);
    setDateTo(to);
    const inLastMonth = receipts.filter((r) => r.date && r.date >= from && r.date <= to);
    if (inLastMonth.length === 0) {
      toast.message("Keine Belege im letzten Monat.");
      return;
    }
    setExporting("package");
    try {
      const res = await exportPackage(from, to);
      await handleExportResponse(res, `Besca_Monatspaket_${from}_bis_${to}.zip`, "Monatspaket erstellt.");
    } catch {
      toast.error("Export fehlgeschlagen.");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-8" data-testid="export-center">
      <div>
        <h1 className="font-heading text-4xl tracking-tight font-medium">Exportieren</h1>
        <p className="text-muted-foreground mt-1">
          Belege eines Zeitraums als Sammel-PDF, ZIP oder Buchhaltungs-CSV exportieren.
        </p>
      </div>

      {/* Zeitraum */}
      <div className="border border-border bg-card" data-testid="export-panel">
        <div className="flex items-center gap-2 px-5 pt-5">
          <CalendarRange className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-lg font-medium">Zeitraum wählen</h2>
        </div>
        <div className="p-5 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <label className="data-label block">Von</label>
            <Input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-white w-44"
              data-testid="filter-date-from"
            />
          </div>
          <div className="space-y-1.5">
            <label className="data-label block">Bis</label>
            <Input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-white w-44"
              data-testid="filter-date-to"
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              const { from, to } = previousMonthRange();
              setDateFrom(from);
              setDateTo(to);
            }}
            className="gap-2 border border-border"
            data-testid="quick-last-month-button"
          >
            <CalendarClock className="h-3.5 w-3.5" /> Letzter Monat
          </Button>
          {periodActive && (
            <Button
              variant="ghost"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              className="gap-2 text-muted-foreground"
              data-testid="reset-period-button"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Zurücksetzen
            </Button>
          )}
        </div>
      </div>

      {/* Stats for the period */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat
          icon={Hash}
          label={periodActive ? "Belege im Zeitraum" : "Belege gesamt"}
          value={stats.count}
          testId="export-stat-count"
        />
        <MiniStat
          icon={Wallet}
          label="Gesamtbetrag"
          values={stats.totals.map((t) => formatMoney(t.grandTotal, t.currency))}
          testId="export-stat-total"
        />
        <MiniStat
          icon={Coins}
          label="Davon Trinkgeld"
          values={stats.totals.map((t) => formatMoney(t.tip, t.currency))}
          testId="export-stat-tip"
        />
        <MiniStat
          icon={Percent}
          label="Enthaltene MwSt."
          values={stats.totals.map((t) => formatMoney(t.vat, t.currency))}
          testId="export-stat-vat"
        />
      </div>

      {/* Export actions */}
      <div className="border border-border bg-card p-5 space-y-4">
        <h2 className="font-heading text-lg font-medium">Export</h2>
        <p className="text-sm text-muted-foreground">
          {periodActive
            ? `${stats.count} Beleg(e) im Zeitraum werden exportiert.`
            : "Kein Zeitraum gewählt – es werden alle Belege exportiert."}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => doExport("zip")}
            disabled={exporting !== "" || stats.count === 0}
            className="gap-2 border border-border"
            data-testid="export-zip-button"
          >
            <FileArchive className="h-4 w-4" />
            {exporting === "zip" ? "Erstelle…" : "PDF Sammlung"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => doExport("csv")}
            disabled={exporting !== "" || stats.count === 0}
            className="gap-2 border border-border"
            data-testid="export-csv-button"
          >
            <Sheet className="h-4 w-4" />
            {exporting === "csv" ? "Erstelle…" : "CSV Buchhaltung"}
          </Button>
          <Button
            onClick={doLastMonth}
            disabled={exporting !== ""}
            className="gap-2"
            data-testid="export-last-month-button"
          >
            <CalendarClock className="h-4 w-4" />
            {exporting === "package" ? "Erstelle…" : "Letztes Monat gesammelt"}
          </Button>
        </div>
        <div className="text-xs text-muted-foreground space-y-1 pt-1 border-t border-border mt-2">
          <div data-testid="last-export-bundle" className="pt-2">
            <span className="font-medium text-foreground">Letzter Beleg-Export:</span>{" "}
            {lastLabel(lastExports.pdf_bundle)}
          </div>
          <div data-testid="last-export-csv">
            <span className="font-medium text-foreground">Letzter CSV-Export:</span>{" "}
            {lastLabel(lastExports.csv_datev)}
          </div>
        </div>
      </div>

      {/* Bereitgestellte Download-Links (große/Hintergrund-Exporte) */}
      {downloadLinks.length > 0 && (
        <div className="border border-border bg-card p-5 space-y-3" data-testid="export-links">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-heading text-lg font-medium">Ihre letzten Export-Links</h2>
            <Button
              variant="ghost"
              onClick={loadDownloads}
              className="gap-1.5 text-muted-foreground h-8 px-2"
              data-testid="export-links-refresh"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Aktualisieren
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Große Exporte werden im Hintergrund erstellt, per E-Mail zugesendet und stehen hier 7 Tage
            zum Download bereit.
          </p>
          <ul className="divide-y divide-border">
            {downloadLinks.map((d) => {
              const dleft = daysLeft(d.expires_at);
              return (
                <li
                  key={d.token}
                  className="flex items-center justify-between gap-3 py-2.5"
                  data-testid={`export-link-${d.token.slice(0, 8)}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.filename}</div>
                    <div className="text-xs text-muted-foreground">
                      Erstellt {formatDate((d.created_at || "").slice(0, 10))} · {formatBytes(d.size)}
                      {dleft !== null && (
                        <>
                          {" · "}
                          <span
                            className={dleft <= 1 ? "text-amber-600 font-medium" : ""}
                            data-testid={`export-link-expiry-${d.token.slice(0, 8)}`}
                          >
                            {dleft === 0
                              ? "läuft heute ab"
                              : `läuft in ${dleft} Tag${dleft === 1 ? "" : "en"} ab`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <a
                    href={`${BACKEND}/api/exports/download/${d.token}`}
                    className="inline-flex items-center gap-1.5 text-sm border border-border rounded-md px-3 py-1.5 hover:bg-muted transition-colors shrink-0"
                    data-testid={`export-link-download-${d.token.slice(0, 8)}`}
                  >
                    <Download className="h-4 w-4" /> Herunterladen
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Preview list */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <h2 className="font-heading text-lg font-medium">Vorschau</h2>
          <SortMenu
            options={SORT_OPTIONS}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortKey={(k) => setSortKey(k)}
            onToggleDir={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className="shrink-0"
          />
        </div>
        {loading ? (
          <div className="border border-border h-40 bg-muted/40 animate-pulse" />
        ) : periodReceipts.length === 0 ? (
          <div className="border border-dashed border-border bg-card text-center text-muted-foreground py-12">
            Keine Belege im gewählten Zeitraum.
          </div>
        ) : (
          <div className="border border-border bg-white" data-testid="export-preview-list">
            <div className="hidden sm:grid grid-cols-[0.9fr_1.8fr_1.2fr_0.9fr] gap-4 px-4 py-3 bg-muted border-b border-border">
              <SortHeader label="Datum" colKey="date" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortHeader label="Lieferant" colKey="supplier" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortHeader label="Zweck" colKey="purpose" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortHeader label="Brutto" colKey="price" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            </div>
            <div className="divide-y divide-border">
              {periodReceipts.map((r) => (
                <div
                  key={r.id}
                  className="grid grid-cols-2 sm:grid-cols-[0.9fr_1.8fr_1.2fr_0.9fr] gap-2 sm:gap-4 px-4 py-2.5 text-sm items-center"
                  data-testid="export-preview-row"
                >
                  <div className="tabular text-muted-foreground">{formatDate(r.date)}</div>
                  <div className="font-medium truncate">{r.supplier || "—"}</div>
                  <div className="hidden sm:block truncate text-muted-foreground">
                    {effectiveValue(r.purpose, r.purpose_other) || "—"}
                  </div>
                  <div className="text-right sm:text-left font-semibold tabular">
                    {formatMoney(r.price, r.currency)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
